import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Cell,
} from "recharts";
import SharingTreeMailingReport from "@/components/SharingTreeMailingReport";
import { Scale, TrendingUp, Calendar, CalendarDays, CalendarRange, Shirt, Users } from "lucide-react";

export default function Reports() {
  const [summary, setSummary] = useState(null);
  const [pounds, setPounds] = useState(null);
  const [coats, setCoats] = useState(null);
  const [retention, setRetention] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chartView, setChartView] = useState("monthly");
  const [retentionWindow, setRetentionWindow] = useState("month");

  useEffect(() => {
    Promise.all([
      api.get("/reports/summary"),
      api.get("/reports/pounds"),
      api.get("/reports/coats"),
      api.get("/reports/household-retention"),
    ])
      .then(([sr, pr, cr, rr]) => {
        setSummary(sr.data);
        setPounds(pr.data);
        setCoats(cr.data);
        setRetention(rr.data);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-sm" style={{ color: "var(--fb-text-muted)" }}>Loading reports…</div>;

  const chartData =
    chartView === "monthly" ? (pounds?.monthly || []).map((d) => ({ label: d.month, pounds: d.pounds }))
    : chartView === "weekly" ? (pounds?.weekly || []).map((d) => ({ label: d.week, pounds: d.pounds }))
    : (pounds?.yearly || []).map((d) => ({ label: d.year, pounds: d.pounds }));

  const retentionData = retention?.[retentionWindow];
  const retentionChartData = retentionData
    ? [
        { label: "New", count: retentionData.new, color: "#4F9060" },
        { label: "Returning", count: retentionData.repeat, color: "#052F66" },
      ]
    : [];

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold mb-2" style={{ color: "#8B918B" }}>Analytics</div>
        <h1 className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: "Outfit" }}>Reports</h1>
      </div>

      <SharingTreeMailingReport />

      {/* Pounds Distributed */}
      <div className="fb-card p-4 sm:p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "var(--fb-primary)15", color: "var(--fb-primary)" }}>
            <Scale className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-xl font-semibold" style={{ fontFamily: "Outfit" }}>Pounds Distributed</h2>
            <p className="text-sm" style={{ color: "var(--fb-text-muted)" }}>Food weight given out at each check-in, based on bag configuration.</p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-6" data-testid="pounds-stat-cards">
          <StatCard label="This Week" value={pounds?.this_week_lbs ?? 0} icon={<CalendarDays className="w-4 h-4" />} />
          <StatCard label="This Month" value={pounds?.this_month_lbs ?? 0} icon={<Calendar className="w-4 h-4" />} />
          <StatCard label="This Year" value={pounds?.this_year_lbs ?? 0} icon={<CalendarRange className="w-4 h-4" />} />
        </div>

        <div className="flex gap-1 mb-4">
          {["monthly", "weekly", "yearly"].map((v) => (
            <button
              key={v}
              onClick={() => setChartView(v)}
              data-testid={`chart-view-${v}`}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors capitalize"
              style={{
                background: chartView === v ? "var(--fb-primary)" : "transparent",
                color: chartView === v ? "#fff" : "var(--fb-text-muted)",
                border: chartView === v ? "none" : "1px solid var(--fb-border)",
              }}
            >
              {v}
            </button>
          ))}
        </div>

        {chartData.length > 0 ? (
          <div style={{ height: 220 }} data-testid="pounds-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E8E8E4" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#8B918B" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#8B918B" }} axisLine={false} tickLine={false}
                  tickFormatter={(v) => `${v} lbs`} width={60} />
                <Tooltip
                  contentStyle={{ borderRadius: 10, fontSize: 13, borderColor: "#E8E8E4" }}
                  formatter={(v) => [`${v} lbs`, "Distributed"]}
                  cursor={{ fill: "#052F6610" }}
                />
                <Bar dataKey="pounds" fill="var(--fb-primary)" radius={[4, 4, 0, 0]} name="Pounds" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="text-sm text-center py-10" style={{ color: "var(--fb-text-muted)" }}>
            No distribution data yet. Data accumulates as clients check in with bag selection.
          </div>
        )}
      </div>

      {/* Coats Distributed */}
      <div className="fb-card p-4 sm:p-6" data-testid="coats-report">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "#7A3B1915", color: "#7A3B19" }}>
            <Shirt className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-xl font-semibold" style={{ fontFamily: "Outfit" }}>Coats Distributed</h2>
            <p className="text-sm" style={{ color: "var(--fb-text-muted)" }}>
              Coats given out during coat distribution season.
              {coats?.current_season ? ` Showing ${coats.current_season} as the featured season.` : ""}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-6" data-testid="coats-stat-cards">
          <CountCard
            label={coats?.current_season || "This Season"}
            value={coats?.current_season_total ?? 0}
            accent="#7A3B19"
          />
          <CountCard label="All-Time Total" value={coats?.all_time_total ?? 0} accent="#7A3B19" />
        </div>

        {coats?.current_season_by_size?.length > 0 && (
          <>
            <p className="text-xs uppercase tracking-wider font-semibold mb-3" style={{ color: "#8B918B" }}>
              {coats.current_season} — by size
            </p>
            <div style={{ height: 180 }} data-testid="coats-size-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={coats.current_season_by_size} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E8E8E4" vertical={false} />
                  <XAxis dataKey="size" tick={{ fontSize: 11, fill: "#8B918B" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#8B918B" }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip contentStyle={{ borderRadius: 10, fontSize: 13, borderColor: "#E8E8E4" }}
                    formatter={(v) => [v, "Coats"]} cursor={{ fill: "#7A3B1910" }} />
                  <Bar dataKey="count" fill="#7A3B19" radius={[4, 4, 0, 0]} name="Coats" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </>
        )}

        {coats?.by_season?.length > 1 && (
          <>
            <p className="text-xs uppercase tracking-wider font-semibold mt-5 mb-3" style={{ color: "#8B918B" }}>
              Season comparison
            </p>
            <div style={{ height: 160 }} data-testid="coats-season-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={coats.by_season} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E8E8E4" vertical={false} />
                  <XAxis dataKey="season" tick={{ fontSize: 11, fill: "#8B918B" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#8B918B" }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip contentStyle={{ borderRadius: 10, fontSize: 13, borderColor: "#E8E8E4" }}
                    formatter={(v) => [v, "Coats"]} cursor={{ fill: "#7A3B1910" }} />
                  <Bar dataKey="total" fill="#B55E30" radius={[4, 4, 0, 0]} name="Coats" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </>
        )}

        {!coats?.current_season_total && !coats?.all_time_total && (
          <div className="text-sm text-center py-8" style={{ color: "var(--fb-text-muted)" }}>
            No coat distributions recorded yet. Data appears once the coat season is active and distributions are logged.
          </div>
        )}
      </div>

      {/* Household Retention */}
      <div className="fb-card p-4 sm:p-6" data-testid="retention-report">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "var(--fb-primary)15", color: "var(--fb-primary)" }}>
            <Users className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-xl font-semibold" style={{ fontFamily: "Outfit" }}>Household Retention</h2>
            <p className="text-sm" style={{ color: "var(--fb-text-muted)" }}>
              New households (first visit ever) vs. returning households, for the selected period.
            </p>
          </div>
        </div>

        <div className="flex gap-1 mb-5">
          {[
            { key: "week", label: "This Week" },
            { key: "month", label: "This Month" },
            { key: "year", label: "This Year" },
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setRetentionWindow(key)}
              data-testid={`retention-window-${key}`}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors"
              style={{
                background: retentionWindow === key ? "var(--fb-primary)" : "transparent",
                color: retentionWindow === key ? "#fff" : "var(--fb-text-muted)",
                border: retentionWindow === key ? "none" : "1px solid var(--fb-border)",
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {retentionData ? (
          <>
            <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-6" data-testid="retention-stat-cards">
              <CountCard label="Total Households" value={retentionData.total} accent="var(--fb-primary)" />
              <CountCard label="New" value={retentionData.new} accent="#4F9060" />
              <CountCard label="Returning" value={retentionData.repeat} accent="#052F66" />
            </div>

            {retentionData.total > 0 ? (
              <div style={{ height: 160 }} data-testid="retention-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={retentionChartData} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E8E8E4" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#8B918B" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: "#8B918B" }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip contentStyle={{ borderRadius: 10, fontSize: 13, borderColor: "#E8E8E4" }}
                      formatter={(v) => [v, "Households"]} cursor={{ fill: "#052F6610" }} />
                    <Bar dataKey="count" radius={[4, 4, 0, 0]} name="Households">
                      {retentionChartData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="text-sm text-center py-8" style={{ color: "var(--fb-text-muted)" }}>
                No check-ins recorded in this period.
              </div>
            )}
          </>
        ) : (
          <div className="text-sm text-center py-8" style={{ color: "var(--fb-text-muted)" }}>
            No retention data available.
          </div>
        )}
      </div>

      {/* Client summary */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4" data-testid="summary-stat-cards">
          <SummaryCard label="Total Clients" value={summary.total_clients} />
          <SummaryCard label="Verified" value={summary.total_address_verified} />
          <SummaryCard label="Visits This Month" value={summary.visits_this_month} />
          <SummaryCard label="Pending Updates" value={summary.pending_updates} accent />
        </div>
      )}

      {/* Monthly visits trend */}
      {summary?.monthly_trend?.length > 0 && (
        <div className="fb-card p-4 sm:p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "var(--fb-primary)15", color: "var(--fb-primary)" }}>
              <TrendingUp className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-xl font-semibold" style={{ fontFamily: "Outfit" }}>Visit Trend</h2>
              <p className="text-sm" style={{ color: "var(--fb-text-muted)" }}>Monthly visits over the past 12 months.</p>
            </div>
          </div>
          <div style={{ height: 200 }} data-testid="visit-trend-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={summary.monthly_trend.map((d) => ({
                  label: d.month,
                  visits: d.visits,
                }))}
                margin={{ top: 4, right: 8, left: 0, bottom: 4 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#E8E8E4" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#8B918B" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#8B918B" }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: 10, fontSize: 13, borderColor: "#E8E8E4" }}
                  formatter={(v) => [v, "Visits"]} cursor={{ fill: "#052F6610" }} />
                <Bar dataKey="visits" fill="#4F9060" radius={[4, 4, 0, 0]} name="Visits" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* By household size */}
      {summary?.by_household_size?.length > 0 && (
        <div className="fb-card p-4 sm:p-6">
          <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: "Outfit" }}>Visits by Household Size</h2>
          <div style={{ height: 180 }} data-testid="by-size-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={summary.by_household_size} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E8E8E4" vertical={false} />
                <XAxis dataKey="size" tick={{ fontSize: 11, fill: "#8B918B" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#8B918B" }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: 10, fontSize: 13, borderColor: "#E8E8E4" }}
                  formatter={(v) => [v, "Visits"]} cursor={{ fill: "#052F6610" }} />
                <Bar dataKey="visits" fill="#7A9E7E" radius={[4, 4, 0, 0]} name="Visits" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, icon }) {
  return (
    <div className="rounded-xl p-4 text-center" style={{ background: "var(--fb-primary)08", border: "1px solid var(--fb-primary)20" }}
      data-testid={`stat-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="flex justify-center mb-1" style={{ color: "var(--fb-primary)" }}>{icon}</div>
      <div className="text-2xl sm:text-3xl font-bold tabular-nums" style={{ fontFamily: "Outfit", color: "var(--fb-primary)" }}>
        {(value ?? 0).toLocaleString()}
      </div>
      <div className="text-[11px] uppercase tracking-wider font-semibold mt-1" style={{ color: "#8B918B" }}>{label} lbs</div>
    </div>
  );
}

function CountCard({ label, value, accent = "var(--fb-primary)" }) {
  return (
    <div className="rounded-xl p-4 text-center" style={{ background: `${accent}08`, border: `1px solid ${accent}25` }}
      data-testid={`count-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="text-2xl sm:text-3xl font-bold tabular-nums" style={{ fontFamily: "Outfit", color: accent }}>
        {(value ?? 0).toLocaleString()}
      </div>
      <div className="text-[11px] uppercase tracking-wider font-semibold mt-1" style={{ color: "#8B918B" }}>{label}</div>
    </div>
  );
}

function SummaryCard({ label, value, accent }) {
  return (
    <div className="fb-card p-4 text-center" data-testid={`summary-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="text-2xl sm:text-3xl font-bold tabular-nums"
        style={{ fontFamily: "Outfit", color: accent ? "var(--fb-error)" : "var(--fb-primary)" }}>
        {(value ?? 0).toLocaleString()}
      </div>
      <div className="text-[11px] uppercase tracking-wider font-semibold mt-1" style={{ color: "#8B918B" }}>{label}</div>
    </div>
  );
}
